import { Stack } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import {
  usePermissionsForCurrentUser,
  useSelectedOrgUnit,
} from '@retail/app/stores/selected-context';
import { FilterChipContainer, ToggleFiltersButton, ToggleFiltersToolbar } from '@retail/components';
import { HierarchyFilterContext } from '@retail/my-assortment/types';
import {
  AddLocalPriceSuggestionsDialog,
  FixedPriceContainer,
  FixedPriceToolbar,
} from '@retail/price-suggestion/containers';
import { useFetchPriceSuggestionFilterContext } from '@retail/price-suggestion/data-access';
import { useFilters } from '@retail/products/hooks';
import { levelFilters, LevelFilters } from '@retail/products/utils';
import { Paper } from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { useMemo } from 'react';

export function FixedPricePage() {
  const { assortmentId } = useSelectedOrgUnit();
  const { userCanCreate } = usePermissionsForCurrentUser();
  const canAddLocalPriceSuggestions = userCanCreate('PRICE_SUGGESTIONS');
  const apiRef = useGridApiRef();

  const { filters } = useFilters<LevelFilters>(levelFilters);

  const {
    data: filterContextDTO,
    isError,
    isFetching,
  } = useFetchPriceSuggestionFilterContext(
    {
      assortmentId,
      supplierNumber: filters.supplierNumber ? Number(filters.supplierNumber) : undefined,
    },
    { suspense: false }
  );
  const filterContext = useMemo(
    () => (filterContextDTO ? HierarchyFilterContext.fromDto(filterContextDTO) : undefined),
    [filterContextDTO]
  );

  const { isOpen, onToggle } = useDisclosure();
  const { isOpen: showFilters, onToggle: toggleFilters } = useDisclosure(true);

  return (
    <Paper sx={{ display: 'flex', flexGrow: 1, flexDirection: 'column', p: 0, overflow: 'hidden' }}>
      <ToggleFiltersToolbar sx={{ mb: 1, justifyContent: 'space-between' }}>
        <FilterChipContainer
          allowedKeys={levelFilters}
          filterOptions={{
            ...filterContext,
            discountGroups: filterContext?.discountGroupsForSupplier,
          }}
        />
        <Stack flexDirection="row" alignItems="flex-start" gap={1}>
          <FixedPriceToolbar
            apiRef={apiRef}
            openAddPricesDialog={canAddLocalPriceSuggestions ? onToggle : undefined}
          />
          <ToggleFiltersButton
            showFilters={showFilters}
            toggleFilters={toggleFilters}
            buttonHeight={50}
            sx={{ mb: -1.1 }}
          />
        </Stack>
      </ToggleFiltersToolbar>

      <FixedPriceContainer
        assortmentId={assortmentId}
        filterContext={filterContext}
        showFilters={showFilters}
        isLoadingFilters={isFetching}
        isErrorFilters={isError}
        apiRef={apiRef}
      />

      {isOpen && (
        <AddLocalPriceSuggestionsDialog open assortmentId={assortmentId} onClose={onToggle} />
      )}
    </Paper>
  );
}
